"use client";

import { useState } from "react";
import { inputClass, labelClass } from "@/components/form";
import { ImageTool, type ImagePlan } from "@/components/ImageTool";
import { Segmented } from "@/components/Segmented";
import { kindOf, kindsLabel, outputName } from "@/lib/files";
import { compressFormatFor, OUTPUT_FORMATS } from "@/lib/image/format";
import { getTool } from "@/lib/tools";
import { cn } from "@/lib/utils";

const tool = getTool("compress-image")!;

type Settings = {
  mode: "quality" | "target";
  /** 10–95 (%). */
  quality: number;
  targetKB: string;
  format: "keep" | "jpg" | "webp";
};

/** FR-22: a quality slider, or a target size in KB found by binary search. */
function planCompress(file: File, s: Settings): ImagePlan {
  const format = compressFormatFor(kindOf(file), s.format);
  const ext = OUTPUT_FORMATS[format].ext;
  const name = () => outputName(file.name, "compressed", ext);
  if (s.mode === "target") {
    const kb = Number(s.targetKB);
    if (!(kb >= 1)) return { error: "Enter a target of at least 1 KB." };
    return { job: { format, maxBytes: Math.floor(kb * 1024) }, name };
  }
  return { job: { format, quality: s.quality / 100 }, name };
}

export function CompressTool() {
  const [s, setS] = useState<Settings>({
    mode: "quality",
    quality: 75,
    targetKB: "100",
    format: "keep",
  });
  const set = (patch: Partial<Settings>) =>
    setS((old) => ({ ...old, ...patch }));

  return (
    <ImageTool
      tool={tool}
      zipName="images-compressed.zip"
      plan={(file) => planCompress(file, s)}
      actionLabel={(n) => (n > 1 ? `Compress ${n} images` : "Compress image")}
      busyVerb="Compressing"
      previewNote={(out, file) => (
        <SavingNote before={file.size} after={out.blob.size} />
      )}
      settings={({ file }) => {
        const format = compressFormatFor(file ? kindOf(file) : null, s.format);
        const kind = file ? kindOf(file) : null;
        return (
          <>
            <Segmented
              label="Compress by"
              stretch
              value={s.mode}
              onChange={(mode) => set({ mode })}
              options={[
                { value: "quality", label: "Quality" },
                { value: "target", label: "Target size" },
              ]}
            />

            {s.mode === "quality" ? (
              <div className={labelClass}>
                <label htmlFor="quality" className="flex justify-between">
                  Quality <span>{s.quality}%</span>
                </label>
                <input
                  id="quality"
                  type="range"
                  min={10}
                  max={95}
                  step={1}
                  value={s.quality}
                  aria-describedby="quality-hint"
                  onChange={(e) => set({ quality: Number(e.target.value) })}
                  className="h-11 w-full accent-brand"
                />
                <span
                  id="quality-hint"
                  className="flex justify-between text-[13px] font-normal text-muted-foreground"
                >
                  <span>Smaller file</span>
                  <span>Better quality</span>
                </span>
              </div>
            ) : (
              <label className={labelClass}>
                Target size (KB)
                <span className="flex items-center gap-2.5 font-normal">
                  <input
                    className={cn(inputClass, "w-32")}
                    inputMode="numeric"
                    value={s.targetKB}
                    onChange={(e) => set({ targetKB: e.target.value })}
                  />
                  <span className="text-sm text-ink">
                    PDF & Pixel finds the best quality under it.
                  </span>
                </span>
              </label>
            )}

            <label className={labelClass}>
              Save as
              <select
                className={inputClass}
                value={s.format}
                onChange={(e) =>
                  set({ format: e.target.value as Settings["format"] })
                }
              >
                <option value="keep">Keep the format when possible</option>
                <option value="jpg">JPG (photos)</option>
                <option value="webp">
                  WebP (smallest, keeps transparency)
                </option>
              </select>
            </label>
            {kind &&
              s.format === "keep" &&
              kindsLabel([kind]) !== OUTPUT_FORMATS[format].label && (
                <p className="text-sm text-ink">
                  {kindsLabel([kind])} files can&apos;t be compressed by
                  quality, so this one is saved as{" "}
                  {OUTPUT_FORMATS[format].label}.
                </p>
              )}
          </>
        );
      }}
    />
  );
}

/** "62% smaller" — or a heads-up when the file got bigger. */
function SavingNote({ before, after }: { before: number; after: number }) {
  const change = Math.round((1 - after / before) * 100);
  return change > 0 ? (
    <p className="text-sm font-semibold text-ink" data-testid="saving">
      {change}% smaller
    </p>
  ) : (
    <p className="text-sm text-ink" data-testid="saving">
      This file is already well compressed. Try a lower quality or a smaller
      target.
    </p>
  );
}
